/**
 * Tiny IndexedDB wrapper used to persist encrypted connection records.
 *
 * No third-party dep; the API surface we need is small enough that wrapping
 * the native API directly is less code than any wrapper library.
 */

const DB_NAME = "agencylens";
const DB_VERSION = 1;
const STORE_CONNECTIONS = "connections";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CONNECTIONS)) {
        db.createObjectStore(STORE_CONNECTIONS, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONNECTIONS, "readonly");
    const store = tx.objectStore(STORE_CONNECTIONS);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result?.value as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

export async function idbPut<T>(key: string, value: T): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONNECTIONS, "readwrite");
    const store = tx.objectStore(STORE_CONNECTIONS);
    const req = store.put({ key, value });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function idbDelete(key: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONNECTIONS, "readwrite");
    const store = tx.objectStore(STORE_CONNECTIONS);
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function idbList(prefix?: string): Promise<string[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONNECTIONS, "readonly");
    const store = tx.objectStore(STORE_CONNECTIONS);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      const keys = (req.result as IDBValidKey[]).filter(
        (k): k is string => typeof k === "string",
      );
      resolve(prefix ? keys.filter((k) => k.startsWith(prefix)) : keys);
    };
    req.onerror = () => reject(req.error);
  });
}
